// src/pages/ResetPassword.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAccessibility } from '../contexts/AccessibilityContext';
import AacButton from '../components/aac/AacButton';
import './ResetPassword.css';
import { useFocusableNavigation } from '../hooks/useFocusableNavigation';

const ResetPassword = () => {
  const { updatePassword } = useAuth();
  const { announce } = useAccessibility();
  useFocusableNavigation();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (password.length < 6) {
      const message = 'Please use a password with at least 6 characters.';
      setError(message);
      announce(message);
      return;
    }

    setLoading(true);
    const { error: updateError } = await updatePassword(password);
    setLoading(false);

    if (updateError) {
      const message = 'Something went wrong updating your password. Please request a new reset link.';
      setError(message);
      announce(message);
      return;
    }

    announce('Password updated successfully');
    navigate('/', { replace: true });
  };

  // The submit button is type="submit" so a real click/Enter/Space already
  // triggers the form's onSubmit natively (required so Enter-in-any-field
  // still submits the form for switch/AAC users). Only the digit-1 AAC
  // shortcut needs to call handleSubmit directly, since it never produces a
  // real click and so never triggers native form submission on its own.
  const handleShortcutSubmit = (event) => {
    if (event.type === 'click') return;
    handleSubmit(event);
  };

  return (
    <div className="auth-page">
      <h1>Set New Password</h1>
      <p className="auth-page__intro">Choose a new password for your account.</p>

      <form className="auth-page__form" onSubmit={handleSubmit}>
        <label htmlFor="reset-password">New Password</label>
        <input
          id="reset-password"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          required
          autoComplete="new-password"
          minLength={6}
        />

        {error && <p className="auth-page__error" role="alert">{error}</p>}

        <div className="auth-page__actions">
          <AacButton type="submit" shortcut={1} onClick={handleShortcutSubmit} disabled={loading}>
            {loading ? 'Saving…' : 'Save New Password'}
          </AacButton>
        </div>
      </form>
    </div>
  );
};

export default ResetPassword;
